<template>
  <div class="planning-view planning">
    <div v-if="loading" class="loading-initial">
      <span class="loading-spinner"></span>
      <span>Chargement de la confirmation...</span>
    </div>

    <div v-else-if="error" class="error-initial alert alert-danger">
      {{ error }}
      <button class="btn btn-primary" @click="retryConfirmation">
        Réessayer
      </button>
    </div>

    <div v-else class="planning-content">
      <div class="container">
        <div class="header">
          <h2>Confirmation de convocation</h2>
        </div>

        <div v-if="meetingDetails" class="meeting-details-grid mt-5 p-4 rounded-lg bg-white shadow-sm border border-gray-200">
          <div class="detail-item">
            <div class="detail-icon text-primary">
              <i :class="hasMeetLink ? 'bi bi-camera-video-fill' : 'bi bi-geo-alt-fill'"></i>
            </div>
            <div class="detail-content">
              <template v-if="hasMeetLink">
                <p class="detail-label text-sm font-medium text-gray-600">Modalité de la convocation</p>
                <p class="detail-value mt-1">
                  <strong>Ce rendez-vous se fera en visioconférence.</strong><br/>
                    Lien : <u>{{normalizedMeetLink}}</u>
                </p>
              </template>
              <template v-else>
                <p class="detail-label text-sm font-medium text-gray-600">Lieu de la convocation</p>
                <p class="detail-value mt-1">
                  <strong>{{ meetingDetails.ofName }}</strong><br/>
                  <span class="text-gray-700">
                    {{ meetingDetails.campusAddress }}<br/>
                    {{ meetingDetails.campusPostalCode }} {{ meetingDetails.campusCityName }}
                  </span>
                </p>
              </template>
            </div>
          </div>

          <div class="detail-item">
            <div class="detail-icon text-primary">
              <i class="bi bi-calendar3"></i>
            </div>
            <div class="detail-content">
              <p class="detail-label text-sm font-medium text-gray-600">Date et heure</p>
              <p class="detail-value mt-1">
                <strong>Le {{ formatDate(meetingDetails.meeting.startAt) }}</strong><br/>
                <span class="text-gray-700">à {{ formatTime(meetingDetails.meeting.startAt) }}</span>
              </p>
            </div>
          </div>
        </div>

        <div class="planning-info">
          <p v-if="null === urlConfirm" class="text-center mb-4">
            Nous avons bien pris en compte votre réponse.
            <br>
          </p>
          <p v-if="status" class="text-center mb-4" v-html="status"></p>

          <div class="d-flex justify-content-center">
            <a
                v-if="urlConfirm"
                :href="urlConfirm"
                class="confirm-btn mt-3 d-flex align-items-center justify-content-center"
            >
              <span v-if="urlConfirm.includes('/accept')" class="icon bi bi-check-circle-fill mr-2"></span>
              <span v-else class="icon bi bi-x-circle-fill mr-2"></span>
              <span class="btn-text">
                {{
                  urlConfirm.includes('/accept')
                      ? "Je confirme accepter l'invitation à la convocation"
                      : "Je confirme refuser l'invitation à la convocation"
                }}
              </span>
            </a>
          </div>


        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {computed, onMounted, ref} from 'vue'
import {useRoute} from 'vue-router'
import '@/assets/styles/global.css'
import {usePlanningStore} from '@/store/planningStore'
import {useDateFormatter} from "@/composables";

const route = useRoute()
const loading = ref(true)
const error = ref(null)
const status = ref(null)
const urlConfirm = ref(null)

const planningStore = usePlanningStore()

const meetingId = ref(null)
const meetingDetails = ref(null)
const normalizedMeetLink = computed(() => meetingDetails.value?.meeting.meetLink?.trim() || '')
const hasMeetLink = computed(() => normalizedMeetLink.value.length > 0)


onMounted(async () => {
  const link = document.createElement('link');
  link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css';
  link.rel = 'stylesheet';
  document.head.appendChild(link);

  meetingId.value = route.query.meetingId

  if (meetingId.value && route.query.ofObjId && route.query.userAccountObjId) {
    const ofObjId = route.query.ofObjId
    const userAccountObjId = route.query.userAccountObjId

    planningStore.fetchMeetingDetails(ofObjId, userAccountObjId, meetingId.value)
        .then((response) => {
          meetingDetails.value = response

        })
        .catch((err) => {
          console.error('Erreur lors du chargement du meeting :', err)
        })
  }

  confirmMeeting()
})

const {formatDate, formatTime} = useDateFormatter()

const confirmMeeting = () => {
  try {
    loading.value = true
    error.value = null

    const {msg, urlConfirm: url} = route.query
    if (msg) {
      status.value = msg
      urlConfirm.value = null
    } else if (url) {
      status.value = "⚠️ Veuillez confirmer votre choix en cliquant sur le bouton ci-dessous."
      urlConfirm.value = url
    } else {
      console.error('Erreur lors de la confirmation du rendez-vous:', 'Paramètres invalides dans l\'URL')
      error.value = 'Paramètres invalides dans l\'URL'
    }
  } catch (err) {
    console.error('Erreur lors de la confirmation du rendez-vous:', err)
    error.value = err.response?.data?.msg || 'Erreur lors de la confirmation du rendez-vous'
  } finally {
    loading.value = false
  }
}

const retryConfirmation = () => {
  confirmMeeting()
}
</script>
<style scoped>
.confirm-btn {
  min-width: 320px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  color: white;
  border: 1px solid transparent;
  border-radius: 12px;
  box-shadow: 0 8px 20px -10px rgba(0, 0, 0, 0.45);
  cursor: pointer;
  user-select: none;
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 26px -14px rgba(0, 0, 0, 0.55);
  filter: brightness(1.04);
}

.confirm-btn:active {
  transform: translateY(0);
  box-shadow: 0 6px 14px -10px rgba(0, 0, 0, 0.5);
}

.confirm-btn:focus-visible {
  outline: 3px solid rgba(13, 110, 253, 0.35);
  outline-offset: 2px;
}

/* Icônes */
.confirm-btn .icon {
  font-size: 1.2rem;
}

/* Texte centré */
.confirm-btn .btn-text {
  white-space: normal;
  text-align: center;
  line-height: 1.35;
}

/* Version accepter */
.confirm-btn[href*="/accept"] {
  background: linear-gradient(180deg, #2d82ff 0%, #0d6efd 100%);
  border-color: #0a57ca;
}

/* Version refuser */
.confirm-btn[href*="/refuse"] {
  background: linear-gradient(180deg, #f25564 0%, #dc3545 100%);
  border-color: #b02a37;
}


.meeting-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin: 0 auto 15px;
}

.detail-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}

.detail-icon {
  font-size: 1.5rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-label {
  margin: 0;
  line-height: 1.4;
}

.detail-value {
  margin: 0;
  line-height: 1.5;
  font-size: 0.95rem;
}

.meeting-link {
  display: inline-block;
  margin-top: 0.25rem;
  color: #0d6efd;
  text-decoration: underline;
  word-break: break-word;
}

/* Responsive : une colonne sur mobile */
@media (max-width: 640px) {
  .meeting-details-grid {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .confirm-btn {
    min-width: 280px;
    font-size: 0.95rem;
    padding: 12px 20px;
  }
}


</style>
